import { ReactNode, useEffect, useRef, useState } from "react";

export interface SceneSlide {
  // `from`/`to` are vestigial from the old scroll-scrub mechanic and ignored.
  from?: number;
  to?: number;
  node: ReactNode;
}

interface Props {
  src: string;
  poster: string;
  slides: SceneSlide[];
  ariaLabel?: string;
  /** Vertical placement of the overlay text. Defaults to "bottom". */
  align?: "top" | "bottom";
  /** Playback speed for the clip (1 = normal). Lower = slower. */
  playbackRate?: number;
}

const SLIDE_INTERVAL = 4500; // ms each caption stays before cross-fading

/**
 * Full-viewport video scene. The clip autoplays and loops, the captions
 * auto-cross-fade on a timer, and the page scrolls normally past it.
 */
export default function PinnedVideoScene({
  src,
  poster,
  slides,
  ariaLabel,
  align = "bottom",
  playbackRate = 1,
}: Props) {
  const [active, setActive] = useState(0);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  // Keep the video at the requested speed (browsers reset playbackRate when
  // the source (re)loads, so re-apply on loadedmetadata too).
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return undefined;
    const apply = () => {
      video.playbackRate = playbackRate;
    };
    apply();
    video.addEventListener("loadedmetadata", apply);
    return () => video.removeEventListener("loadedmetadata", apply);
  }, [playbackRate]);

  useEffect(() => {
    if (slides.length <= 1) return undefined;

    const reduce =
      typeof window !== "undefined" &&
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) return undefined; // keep the first caption, no auto-advance

    const id = window.setInterval(() => setActive((i) => (i + 1) % slides.length), SLIDE_INTERVAL);
    return () => window.clearInterval(id);
  }, [slides.length]);

  return (
    <section className={`lpv2-scene lpv2-scene--${align}`} aria-label={ariaLabel}>
      <video
        ref={videoRef}
        className="lpv2-scene-video"
        src={src}
        poster={poster}
        muted
        playsInline
        loop
        autoPlay
        preload="auto"
        aria-hidden
      />
      <div className="lpv2-scene-vignette" />
      <div className="lpv2-scene-overlay">
        {slides.map((slide, i) => (
          <div key={i} className={`lpv2-scene-slide${i === active ? " is-active" : ""}`}>
            {slide.node}
          </div>
        ))}
      </div>
    </section>
  );
}
